import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { ComponentType, ReactNode } from 'react';

import { EntityCsvConfigKey } from '~hooks/useCSVImportExport/useEntityCsvConfig';
import { AccountsDialog } from '~pages/Settings/Accounts/AccountsDialog';
import MaterialDialog from '~pages/Settings/Administration/MaterialsManagement/MaterialDialog';
import SiteDialog from '~pages/Settings/Administration/Sites/SiteDialog';
import { userHasAccessToConfigurationManagement } from '~pages/Settings/Configuration/ConfigurationManagement';
import DepartmentDialog from '~pages/Settings/DepartmentManagement/DepartmentDialog';
import AddOnDialog from '~pages/Settings/RatesManagement/AddOnCharges/components/AddOnDialog';
import { CompanyOnboardingRecordsStepKey } from '~queries/companyOnboardingRecords';
import { ROUTES } from '~router/routePaths';
import UserStore from '~store/UserStore';
import type EN_translation from '~translations/en/translation.json';

import { CreateDialogProps } from './records/CompanyOnboardingRecordsPanel';
import AgentAccessStep from './steps/AgentAccessStep';
import ConfigurationsStep from './steps/ConfigurationsStep';
import FreightRatesStep from './steps/FreightRatesStep';
import GeofenceShapeGuide from './steps/GeofenceShapeGuide';
import IntegrationsStep from './steps/IntegrationsStep';
import RoleDefinitionsLink from './steps/RoleDefinitionsLink';
import StepLinkOutCard from './steps/StepLinkOutCard';
import StepSectionCard from './steps/StepSectionCard';

type ConceptCardKey = keyof typeof EN_translation.company_onboarding.concept_cards;

type StepAccessCheck = (userStore: UserStore) => boolean;

interface StepContentProps {
  companyId: string;
  settingsLink: string;
  stepKey: CompanyOnboardingStepKey;
}

interface StepRecordsPanelOptions {
  CreateDialog?: ComponentType<CreateDialogProps>;
  detailColumnLabelKey?: `form_fields.${keyof typeof EN_translation.form_fields}`;
  titleKey?: string;
}

interface CompanyOnboardingStepConfig {
  WorkArea?: ComponentType<StepContentProps>;
  afterWorkArea?: ReactNode;
  beforeWorkArea?: ReactNode;
  canAccess: StepAccessCheck;
  conceptCardFooters?: Partial<Record<ConceptCardKey, ReactNode>>;
  conceptCards?: ConceptCardKey[];
  csvConfigKey?: EntityCsvConfigKey;
  docsPath: string;
  phase: 'build_your_workspace' | 'connect_your_tools';
  settingsLink: string;
}

type CompanyOnboardingSteps = {
  [StepKey in CompanyOnboardingStepKey]: CompanyOnboardingStepConfig &
    (StepKey extends CompanyOnboardingRecordsStepKey
      ? { records: StepRecordsPanelOptions }
      : { records?: never });
};

const CustomerCreateDialog = ({ isOpen, ...props }: CreateDialogProps) => (
  <AccountsDialog {...props} open={isOpen} source="customers-page" />
);

const VendorCreateDialog = ({ isOpen, ...props }: CreateDialogProps) => (
  <AccountsDialog {...props} open={isOpen} source="vendors-page" />
);

const SiteCreateDialog = (props: CreateDialogProps) => (
  <SiteDialog {...props} siteId={null} />
);

const hasUserAccess =
  (accessKey: keyof ReturnType<UserStore['getAccess']>): StepAccessCheck =>
  (userStore) =>
    userStore.getAccess()[accessKey];

const canManageAnIntegration: StepAccessCheck = (userStore) => {
  const { canManageAccountingIntegration, canManageTelematicsIntegration } =
    userStore.getPermissions();

  return canManageAccountingIntegration || canManageTelematicsIntegration;
};

// Users, drivers, and equipment get no Add button: their Settings pages create records
// with side effects (compliance documents, company shares, home sites) a copy would drop.
export const companyOnboardingSteps: CompanyOnboardingSteps = {
  [CompanyOnboardingStepKey.AGENT_ACCESS]: {
    WorkArea: AgentAccessStep,
    canAccess: hasUserAccess('manageM2MClients'),
    docsPath: 'onboarding/agent-configurations',
    phase: 'connect_your_tools',
    settingsLink: ROUTES.settings.apiClients,
  },
  [CompanyOnboardingStepKey.INTEGRATIONS]: {
    WorkArea: IntegrationsStep,
    canAccess: canManageAnIntegration,
    docsPath: 'onboarding/integrations',
    phase: 'connect_your_tools',
    settingsLink: ROUTES.settings.integrations.base,
  },
  [CompanyOnboardingStepKey.USERS]: {
    canAccess: hasUserAccess('manageUser'),
    conceptCardFooters: { role: <RoleDefinitionsLink /> },
    conceptCards: ['user', 'role'],
    csvConfigKey: 'users',
    docsPath: 'onboarding/master-data/users',
    phase: 'build_your_workspace',
    records: {
      detailColumnLabelKey: 'form_fields.role',
    },
    settingsLink: ROUTES.settings.userManagement,
  },
  [CompanyOnboardingStepKey.CUSTOMERS]: {
    canAccess: hasUserAccess('manageAccounts'),
    conceptCards: ['customer', 'external_id'],
    csvConfigKey: 'customers',
    docsPath: 'onboarding/master-data/customers',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: CustomerCreateDialog,
      detailColumnLabelKey: 'form_fields.external_id',
    },
    settingsLink: ROUTES.settings.customers,
  },
  [CompanyOnboardingStepKey.VENDORS]: {
    canAccess: hasUserAccess('manageAccounts'),
    conceptCards: ['multi_truck_vendor', 'owner_operator'],
    csvConfigKey: 'vendors',
    docsPath: 'onboarding/master-data/vendors',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: VendorCreateDialog,
      detailColumnLabelKey: 'form_fields.external_id',
    },
    settingsLink: ROUTES.settings.vendors,
  },
  [CompanyOnboardingStepKey.EQUIPMENT]: {
    canAccess: hasUserAccess('manageEquipment'),
    conceptCards: ['equipment_type', 'owned_by'],
    csvConfigKey: 'equipment',
    docsPath: 'onboarding/master-data/equipment',
    phase: 'build_your_workspace',
    records: {
      detailColumnLabelKey: 'form_fields.equipment_type',
    },
    settingsLink: ROUTES.settings.equipmentManagement,
  },
  [CompanyOnboardingStepKey.DRIVERS]: {
    canAccess: hasUserAccess('manageUser'),
    conceptCards: ['employee_driver', 'outside_driver'],
    csvConfigKey: 'drivers',
    docsPath: 'onboarding/master-data/drivers',
    phase: 'build_your_workspace',
    records: {
      detailColumnLabelKey: 'form_fields.phone',
    },
    settingsLink: ROUTES.settings.drivers,
  },
  [CompanyOnboardingStepKey.SITES]: {
    beforeWorkArea: <GeofenceShapeGuide />,
    canAccess: hasUserAccess('manageSites'),
    conceptCards: ['site', 'geofence'],
    csvConfigKey: 'sites',
    docsPath: 'onboarding/master-data/sites',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: SiteCreateDialog,
      detailColumnLabelKey: 'form_fields.address',
    },
    settingsLink: ROUTES.settings.sitesManagement,
  },
  [CompanyOnboardingStepKey.MATERIALS]: {
    canAccess: hasUserAccess('manageMaterials'),
    conceptCards: ['material_type', 'material'],
    csvConfigKey: 'materials',
    docsPath: 'onboarding/master-data/materials',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: MaterialDialog,
      detailColumnLabelKey: 'form_fields.material_type',
    },
    settingsLink: ROUTES.settings.materialsManagement,
  },
  [CompanyOnboardingStepKey.MATERIAL_RATES]: {
    WorkArea: StepLinkOutCard,
    canAccess: hasUserAccess('manageRates'),
    conceptCards: ['sell_price', 'buy_price'],
    docsPath: 'workflows/manage-rates',
    phase: 'build_your_workspace',
    settingsLink: ROUTES.settings.materialRatesManagement,
  },
  [CompanyOnboardingStepKey.FREIGHT_RATES]: {
    WorkArea: FreightRatesStep,
    canAccess: hasUserAccess('manageRates'),
    conceptCards: ['charge_and_cost'],
    docsPath: 'concepts/rates-add-ons',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: AddOnDialog,
      detailColumnLabelKey: 'form_fields.external_id',
      titleKey:
        'company_onboarding.modules.freight_rates.work_area.sections.add_ons.title',
    },
    settingsLink: ROUTES.settings.addOnCharges,
  },
  [CompanyOnboardingStepKey.DEPARTMENTS_CLASSES]: {
    WorkArea: StepLinkOutCard,
    afterWorkArea: (
      <StepSectionCard
        copyKey="company_onboarding.modules.departments_classes.labels"
        link={ROUTES.settings.labelsManagement}
        testId="company-onboarding-labels"
      />
    ),
    canAccess: hasUserAccess('manageDepartments'),
    conceptCards: ['department', 'service_class'],
    docsPath: 'onboarding/master-data/departments-labels-classes',
    phase: 'build_your_workspace',
    records: {
      CreateDialog: DepartmentDialog,
    },
    settingsLink: ROUTES.settings.departmentsManagement,
  },
  [CompanyOnboardingStepKey.FUEL]: {
    WorkArea: StepLinkOutCard,
    canAccess: hasUserAccess('manageRates'),
    conceptCards: ['fuel_index', 'fuel_schedule'],
    docsPath: 'specialized/fuel-surcharges',
    phase: 'build_your_workspace',
    settingsLink: ROUTES.settings.fuelSurchargeScheduleManagement,
  },
  [CompanyOnboardingStepKey.CONFIGURATIONS]: {
    WorkArea: ConfigurationsStep,
    canAccess: userHasAccessToConfigurationManagement,
    docsPath: 'onboarding/platform-configurations',
    phase: 'build_your_workspace',
    settingsLink: ROUTES.settings.configuration,
  },
};

export const isCompanyOnboardingStepKey = (
  value: string | undefined,
): value is CompanyOnboardingStepKey =>
  value !== undefined && Object.hasOwn(companyOnboardingSteps, value);
